// src/lib/render/diagram-alt-text.ts
//
// Text equivalents for every figure the shared <Diagram> dispatcher draws —
// lessons, the tutor, rich explanations, and (the case that matters most)
// question stems, where a screen-reader user has nothing else to answer from.
//
// Each figure gets two things:
//   - `name`    a short accessible name ("Graph of two lines in the xy-plane")
//   - `details` the sentences a student needs to answer from the figure: every
//               label drawn on it, and for a graph the points it passes through,
//               where it crosses the axes, its vertex, where curves intersect.
// <Diagram> renders the name on the figure and the details as visually hidden
// text right after it (wired up with aria-describedby), so the description is in
// the reading order — before the answer choices — and not only in an attribute
// a screen reader may skip.
//
// THE RULE: DESCRIBE WHAT IS DRAWN, NOT THE NUMBERS THAT DREW IT
// ---------------------------------------------------------------
// The geometry renderers take numeric params that only set the SHAPE
// (`base: 24`) and separate label strings that are what the student SEES
// (`heightLabel: "?"`). The previous version of this file read the numeric
// params, so a right triangle whose height is the unknown was described as
// "base 7, height 24" — the answer, handed to screen-reader users only — and a
// triangle labelled 55°, 80°, x° was read out as "55°, 80°, and 45°". Labels
// are what we read now; drawing params are only used for things a sighted
// student can also see (which side is horizontal, which sides are parallel).
//
// For function graphs the same rule means COORDINATES, not EQUATIONS. A sighted
// student reads points off the grid; many graph questions ask "which equation
// represents the line?", and reading out "y = 2x − 3" would answer them. So a
// graph is described by its axes, the points each curve passes through, its
// intercepts, vertex or turning points, and where the curves intersect — the
// information on the page, and enough to recover the equation the way a sighted
// student would.
//
// Returns `null` for diagram types whose content is already accessible DOM
// (data tables, reading passages, decision trees); <Diagram> leaves those
// unwrapped so their native semantics reach assistive tech.

import type { LessonDiagram } from '@/lib/study-plan/lesson-generation'
import {
  evalFn,
  graphWindow,
  overlayCurves,
  resolveHighlightPoints,
  riseRunAnnotation,
  vertexExtremum,
  type FnSpec,
  type FunctionGraphParams,
  type GraphWindow,
} from './function-graph-model'

type Params = Record<string, unknown>

export type FigureDescription = {
  /** Short accessible name for the figure. */
  name: string
  /** The figure's information, one sentence per entry, in reading order. */
  details: string[]
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined
}

function finite(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

function numList(v: unknown): number[] {
  return Array.isArray(v)
    ? v.filter((x): x is number => typeof x === 'number' && Number.isFinite(x))
    : []
}

function strList(v: unknown): (string | undefined)[] {
  return Array.isArray(v) ? v.map((x) => str(x)) : []
}

/**
 * A label as it should be read. A bare "?" is dropped by most screen readers
 * at their default punctuation level, which would turn "the hypotenuse is
 * labeled ?" into "the hypotenuse is labeled" — so it is said in words.
 */
export function spokenLabel(label: string): string {
  return label.trim() === '?' ? 'with a question mark' : label.trim()
}

/** "a", "a and b", "a, b, and c". */
function list(items: string[]): string {
  if (items.length <= 1) return items[0] ?? ''
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

const capitalize = (t: string) => t.charAt(0).toUpperCase() + t.slice(1)

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight']
const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth']

const EPS = 1e-6

/**
 * A number as it should be heard. A minus sign is spoken as "negative": a
 * hyphen-minus before a digit is not voiced by every screen reader at its
 * default punctuation level, and "(-3, 0)" heard as "3, 0" is a wrong answer.
 * Values that are not a round two-decimal number are marked "about".
 */
export function spokenNumber(n: number): string {
  const r = Math.round(n)
  let v = Math.abs(n - r) < EPS ? r : n
  if (Object.is(v, -0)) v = 0
  const twoDp = Math.round(v * 100) / 100
  const exact = Math.abs(v - twoDp) < EPS
  const text = String(Math.abs(twoDp))
  const signed = twoDp < 0 ? `negative ${text}` : text
  return exact ? signed : `about ${signed}`
}

/** "(2, negative 3)" — "about" hoisted to the front if either coordinate is approximate. */
export function spokenPoint(x: number, y: number): string {
  const sx = spokenNumber(x)
  const sy = spokenNumber(y)
  const approx = sx.startsWith('about ') || sy.startsWith('about ')
  const strip = (s: string) => s.replace(/^about /, '')
  return `${approx ? 'about ' : ''}(${strip(sx)}, ${strip(sy)})`
}

// ---------------------------------------------------------------------------
// Function graphs
// ---------------------------------------------------------------------------

const FN_NOUN: Record<string, string> = {
  linear: 'line',
  quadratic: 'parabola',
  exponential: 'exponential curve',
  cubic: 'cubic curve',
  absolute: 'absolute-value graph',
}

function kindOf(s: FnSpec): string {
  return s.kind && FN_NOUN[s.kind] ? s.kind : 'linear'
}

/** Real zeros of h on [lo, hi]: sign changes, plus touch points (|h| dips to 0). */
export function findZeros(h: (x: number) => number, lo: number, hi: number): number[] {
  if (!(hi > lo)) return []
  const N = 2000
  const step = (hi - lo) / N
  const xs: number[] = []
  const vs: number[] = []
  for (let i = 0; i <= N; i++) {
    const x = lo + step * i
    xs.push(x)
    vs.push(h(x))
  }
  const finiteVals = vs.filter(Number.isFinite)
  // Identically zero (two coincident curves): there is no point to name.
  if (finiteVals.length && finiteVals.every((v) => Math.abs(v) < 1e-12)) return []
  const scale = Math.max(1, ...finiteVals.map((v) => Math.abs(v)))
  const roots: number[] = []
  const push = (x: number) => {
    const r = Math.round(x)
    const snapped = Math.abs(x - r) < 1e-6 ? r : x
    if (!roots.some((y) => Math.abs(y - snapped) < 1e-4)) roots.push(snapped)
  }
  for (let i = 0; i <= N; i++) {
    const v = vs[i]
    if (!Number.isFinite(v)) continue
    if (v === 0) {
      push(xs[i])
      continue
    }
    // Sign change between i and i+1 → bisect.
    if (i < N && Number.isFinite(vs[i + 1]) && vs[i + 1] !== 0 && v * vs[i + 1] < 0) {
      let a = xs[i]
      let b = xs[i + 1]
      let fa = v
      for (let k = 0; k < 80; k++) {
        const m = (a + b) / 2
        const fm = h(m)
        if (fm === 0) {
          a = b = m
          break
        }
        if (fa * fm < 0) b = m
        else {
          a = m
          fa = fm
        }
      }
      push((a + b) / 2)
      continue
    }
    // A local minimum of |h| close to 0 is a touch point (e.g. a parabola whose
    // vertex sits on the axis) that a sign test never sees.
    if (i > 0 && i < N) {
      const av = Math.abs(v)
      if (
        av <= Math.abs(vs[i - 1]) &&
        av <= Math.abs(vs[i + 1]) &&
        av < 1e-3 * scale &&
        v * vs[i - 1] > 0 &&
        v * vs[i + 1] > 0
      ) {
        let a = xs[i - 1]
        let b = xs[i + 1]
        for (let k = 0; k < 100; k++) {
          const m1 = a + (b - a) / 3
          const m2 = b - (b - a) / 3
          if (Math.abs(h(m1)) < Math.abs(h(m2))) b = m2
          else a = m1
        }
        const x = (a + b) / 2
        if (Math.abs(h(x)) < 1e-9 * scale) push(x)
      }
    }
  }
  return roots.sort((a, b) => a - b)
}

const inWindow = (w: GraphWindow, x: number, y: number) =>
  x >= w.xMin - EPS && x <= w.xMax + EPS && y >= w.yMin - EPS && y <= w.yMax + EPS

const isLattice = (v: number) => Math.abs(v - Math.round(v)) < 1e-9

/** Up to `max` integer-coordinate points on the curve inside the window, spread out. */
function latticePoints(
  s: FnSpec,
  w: GraphWindow,
  exclude: { x: number; y: number }[],
  max: number,
): { x: number; y: number }[] {
  const cands: { x: number; y: number }[] = []
  for (let x = Math.ceil(w.xMin); x <= Math.floor(w.xMax); x++) {
    const y = evalFn(s, x)
    if (!Number.isFinite(y) || !isLattice(y)) continue
    const yr = Math.round(y)
    if (!inWindow(w, x, yr)) continue
    if (exclude.some((e) => Math.abs(e.x - x) < EPS && Math.abs(e.y - yr) < EPS)) continue
    cands.push({ x, y: yr })
  }
  if (cands.length <= max) return cands
  // Spread the picks across the visible part of the curve.
  const out: { x: number; y: number }[] = []
  for (let i = 0; i < max; i++) {
    const idx = Math.round((i * (cands.length - 1)) / Math.max(1, max - 1))
    if (!out.includes(cands[idx])) out.push(cands[idx])
  }
  return out
}

/** Sentences describing one plotted function, as drawn in window `w`. */
function describeCurve(s: FnSpec, w: GraphWindow, ref: string): string[] {
  const kind = kindOf(s)
  const out: string[] = []
  const Ref = capitalize(ref)
  const f = (x: number) => evalFn(s, x)
  const key: { x: number; y: number }[] = []
  const facts: string[] = []

  // Shape / direction.
  if (kind === 'linear') {
    const m = finite(s.slope) ?? 1
    if (Math.abs(m) < EPS) facts.push(`${Ref} is horizontal`)
    else facts.push(`${Ref} ${m > 0 ? 'rises' : 'falls'} from left to right`)
  } else if (kind === 'quadratic' || kind === 'absolute') {
    const v = vertexExtremum(s)
    if (v) {
      const shape = kind === 'quadratic' ? 'opens' : 'is a V shape opening'
      facts.push(`${Ref} ${shape} ${v.opensUp ? 'upward' : 'downward'}`)
      if (inWindow(w, v.x, v.y)) {
        facts.push(
          `its vertex, the ${v.opensUp ? 'lowest' : 'highest'} point, is at ${spokenPoint(v.x, v.y)}`,
        )
        key.push(v)
      }
    } else {
      facts.push(`${Ref} is horizontal`)
    }
  } else if (kind === 'exponential') {
    const a = finite(s.a) ?? 1
    const base = finite(s.base) ?? 2
    const c = finite(s.c) ?? 0
    const growth = a * Math.log(base)
    if (Math.abs(growth) < EPS) facts.push(`${Ref} is horizontal`)
    else {
      facts.push(`${Ref} ${growth > 0 ? 'increases' : 'decreases'} from left to right`)
      // The asymptote is on the side where b^x → 0.
      const side = base > 1 ? 'left' : 'right'
      facts.push(`toward the ${side} it levels off, approaching the line y = ${spokenNumber(c)}`)
    }
  } else if (kind === 'cubic') {
    // Turning points: roots of 3a x² + 2b x + c.
    const a = finite(s.a) ?? 1
    const b = finite(s.b) ?? 0
    const c = finite(s.c) ?? 0
    const A = 3 * a
    const B = 2 * b
    const disc = B * B - 4 * A * c
    const turns: { x: number; y: number; max: boolean }[] = []
    if (Math.abs(A) > EPS && disc > EPS) {
      for (const x of [(-B - Math.sqrt(disc)) / (2 * A), (-B + Math.sqrt(disc)) / (2 * A)]) {
        // second derivative 6a x + 2b < 0 → local max
        turns.push({ x, y: f(x), max: 6 * a * x + 2 * b < 0 })
      }
    }
    const shown = turns.sort((p, q) => p.x - q.x).filter((t) => inWindow(w, t.x, t.y))
    for (const t of shown) key.push(t)
    facts.push(
      `${Ref} ${a > 0 ? 'rises' : 'falls'} overall from left to right` +
        (shown.length
          ? `, turning at ${list(shown.map((t) => `a local ${t.max ? 'maximum' : 'minimum'} at ${spokenPoint(t.x, t.y)}`))}`
          : ''),
    )
  }
  out.push(facts.join('; ') + '.')

  // Intercepts the student can see.
  const ints: string[] = []
  const y0 = f(0)
  const yInt = Number.isFinite(y0) && inWindow(w, 0, y0)
  const origin = yInt && Math.abs(y0) < EPS
  let onAxis = false
  let xs: number[]
  if (kind === 'linear') {
    // Exact, and safe for a line lying along the axis (every point a "zero").
    const m = finite(s.slope) ?? 1
    const b = finite(s.intercept) ?? 0
    onAxis = Math.abs(m) < EPS && Math.abs(b) < EPS
    xs = Math.abs(m) < EPS ? [] : [-b / m]
  } else {
    xs = findZeros(f, w.xMin, w.xMax)
  }
  // The origin is said once, as the origin.
  xs = xs.filter((x) => inWindow(w, x, 0) && !(origin && Math.abs(x) < EPS))
  if (onAxis) {
    ints.push('lies along the x-axis')
  } else {
    if (origin) ints.push('passes through the origin at (0, 0)')
    else if (yInt) ints.push(`crosses the y-axis at ${spokenPoint(0, y0)}`)
    if (xs.length) ints.push(`meets the x-axis at ${list(xs.map((x) => spokenPoint(x, 0)))}`)
    else if (!origin) ints.push('does not meet the x-axis within the graph')
  }
  if (yInt) key.push({ x: 0, y: y0 })
  for (const x of xs) key.push({ x, y: 0 })
  if (ints.length) out.push(`It ${list(ints)}.`)

  // A few more points read off the grid, so slope and shape can be recovered.
  const want = kind === 'linear' ? Math.max(0, 3 - key.length) : Math.max(2, 5 - key.length)
  const more = latticePoints(s, w, key, want)
  if (more.length) {
    out.push(
      `It ${key.length ? 'also ' : ''}passes through ${list(more.map((p) => spokenPoint(p.x, p.y)))}.`,
    )
  } else if (key.length < 2) {
    // No grid points at all: give two readings so the curve is still placed.
    const xa = Math.ceil(w.xMin)
    const xb = Math.floor(w.xMax)
    const pts = [xa, xb]
      .filter((x, i, arr) => arr.indexOf(x) === i)
      .map((x) => ({ x, y: f(x) }))
      .filter((p) => Number.isFinite(p.y) && inWindow(w, p.x, p.y))
    if (pts.length) out.push(`It passes through ${list(pts.map((p) => spokenPoint(p.x, p.y)))}.`)
  }
  return out
}

function curveRefs(curves: FnSpec[]): string[] {
  const totals = new Map<string, number>()
  for (const c of curves) totals.set(kindOf(c), (totals.get(kindOf(c)) ?? 0) + 1)
  const seen = new Map<string, number>()
  return curves.map((c) => {
    const k = kindOf(c)
    const i = seen.get(k) ?? 0
    seen.set(k, i + 1)
    return (totals.get(k) ?? 0) > 1
      ? `the ${ORDINALS[i] ?? `#${i + 1}`} ${FN_NOUN[k]}`
      : `the ${FN_NOUN[k]}`
  })
}

function graphName(curves: FnSpec[]): string {
  const counts = new Map<string, number>()
  for (const c of curves) counts.set(kindOf(c), (counts.get(kindOf(c)) ?? 0) + 1)
  const parts = [...counts.entries()].map(([k, n]) => {
    const noun = FN_NOUN[k]
    if (n === 1) return `${/^[aeiou]/.test(noun) ? 'an' : 'a'} ${noun}`
    return `${NUMBER_WORDS[n] ?? n} ${noun}s`
  })
  return `Graph of ${list(parts)} in the xy-plane`
}

/** Strip a coordinate pair from a point label: "L(2,1)" → "L"; "(3,3)" → "". */
function pointName(label: string, x: number, y: number): string {
  const m = label.match(/^\s*([^()]*?)\s*\(\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*\)\s*$/)
  if (m && Math.abs(Number(m[2]) - x) < 1e-6 && Math.abs(Number(m[3]) - y) < 1e-6) return m[1]
  return label
}

function describeFunctionGraph(p: FunctionGraphParams): FigureDescription {
  const w = graphWindow(p)
  const curves: FnSpec[] = [p, ...overlayCurves(p)]
  const refs = curveRefs(curves)
  const details: string[] = []

  details.push(
    `The x-axis runs from ${spokenNumber(w.xMin)} to ${spokenNumber(w.xMax)} and the y-axis from ` +
      `${spokenNumber(w.yMin)} to ${spokenNumber(w.yMax)}, with grid lines every 1 unit.`,
  )

  curves.forEach((c, i) => details.push(...describeCurve(c, w, refs[i])))

  // Where the curves cross — the solution of a system, read off the picture.
  for (let i = 0; i < curves.length; i++) {
    for (let j = i + 1; j < curves.length; j++) {
      const a = curves[i]
      const b = curves[j]
      const xs = findZeros((x) => evalFn(a, x) - evalFn(b, x), w.xMin, w.xMax)
        .map((x) => ({ x, y: evalFn(a, x) }))
        .filter((pt) => inWindow(w, pt.x, pt.y))
      const pair =
        curves.length === 2 && kindOf(a) === kindOf(b)
          ? `The two ${FN_NOUN[kindOf(a)]}s`
          : `${capitalize(refs[i])} and ${refs[j]}`
      details.push(
        xs.length
          ? `${pair} intersect at ${list(xs.map((pt) => spokenPoint(pt.x, pt.y)))}.`
          : `${pair} do not intersect within the graph.`,
      )
    }
  }

  const marked = resolveHighlightPoints(p).filter((pt) => inWindow(w, pt.x, pt.y))
  if (marked.length) {
    const items = marked.map((pt) => {
      const lab = str(pt.label)
      const name = lab ? pointName(lab, pt.x, pt.y) : ''
      const at = spokenPoint(pt.x, pt.y)
      if (!lab || !name) return at
      return name === lab ? `${at}, labeled ${spokenLabel(lab)}` : `${spokenLabel(name)} at ${at}`
    })
    details.push(`Marked point${marked.length > 1 ? 's' : ''}: ${list(items)}.`)
  }

  const rr = riseRunAnnotation(p)
  if (rr) {
    details.push(
      `A slope triangle is drawn from ${spokenPoint(rr.from.x, rr.from.y)} to ` +
        `${spokenPoint(rr.to.x, rr.to.y)}, labeled run = ${spokenNumber(rr.run)} and rise = ${spokenNumber(rr.rise)}.`,
    )
  }

  if (p.shade === 'above' || p.shade === 'below') {
    details.push(`The region ${p.shade} ${refs[0]} is shaded.`)
  }

  return { name: str(p.label) ?? graphName(curves), details }
}

// ---------------------------------------------------------------------------
// Other diagram types
// ---------------------------------------------------------------------------

/** Compass position of a point on a circle at `deg` (0° = right, CCW). */
function circlePosition(deg: number): string {
  const d = (((Math.round(deg / 45) * 45) % 360) + 360) % 360
  const names: Record<number, string> = {
    0: 'right',
    45: 'upper right',
    90: 'top',
    135: 'upper left',
    180: 'left',
    225: 'lower left',
    270: 'bottom',
    315: 'lower right',
  }
  return names[d]
}

const labeled = (thing: string, label: string | undefined) =>
  label ? `${thing}, labeled ${spokenLabel(label)}` : thing

function describeCircle(p: Params): FigureDescription {
  const details: string[] = []
  const points = (Array.isArray(p.points) ? p.points : []).filter(
    (pt): pt is { angleDeg: number; label?: unknown } =>
      !!pt && typeof (pt as { angleDeg?: unknown }).angleDeg === 'number',
  )
  /** Name a position on the circle: a labelled point if one sits there. */
  const at = (deg: number) => {
    const hit = points.find((pt) => Math.abs(pt.angleDeg - deg) < 0.5 && str(pt.label))
    return hit ? `point ${str(hit.label)}` : `the ${circlePosition(deg)} of the circle`
  }
  const centralAngle = p.centralAngle as Params | undefined
  const showCenter =
    p.showCenter !== false &&
    (p.showCenter === true ||
      p.radiusLabel !== undefined ||
      p.centralAngle !== undefined ||
      p.diameterLabel !== undefined)
  const centerLabel = typeof p.centerLabel === 'string' ? p.centerLabel : 'O'
  if (showCenter)
    details.push(centerLabel ? `The center is labeled ${centerLabel}.` : 'The center is marked.')
  if (points.length) {
    const items = points.map((pt) => {
      const l = str(pt.label)
      return l
        ? `${l} at the ${circlePosition(pt.angleDeg)}`
        : `one at the ${circlePosition(pt.angleDeg)}`
    })
    details.push(`Point${points.length > 1 ? 's' : ''} on the circle: ${list(items)}.`)
  }
  if (p.radiusLabel !== undefined) {
    const a = finite(p.radiusAngle) ?? 90
    details.push(`${labeled(`A radius is drawn from the center to ${at(a)}`, str(p.radiusLabel))}.`)
  }
  if (p.diameterLabel !== undefined) {
    details.push(`${labeled('A horizontal diameter is drawn', str(p.diameterLabel))}.`)
  }
  if (centralAngle && typeof centralAngle === 'object') {
    const from = finite(centralAngle.from)
    const to = finite(centralAngle.to)
    if (from != null && to != null) {
      let s = `A central angle is formed by radii to ${at(from)} and ${at(to)}`
      if (str(centralAngle.label))
        s += `; the angle is labeled ${spokenLabel(str(centralAngle.label)!)}`
      if (centralAngle.shade) s += '; the sector between them is shaded'
      details.push(`${s}.`)
    }
  }
  const ins = p.inscribedAngle as Params | undefined
  if (ins && typeof ins === 'object') {
    const v = finite(ins.vertex)
    const from = finite(ins.from)
    const to = finite(ins.to)
    if (v != null && from != null && to != null) {
      details.push(
        `${labeled(`An inscribed angle has its vertex at ${at(v)}, with chords to ${at(from)} and ${at(to)}`, str(ins.label))}.`,
      )
    }
  }
  for (const ch of Array.isArray(p.chords) ? (p.chords as Params[]) : []) {
    const from = finite(ch?.from)
    const to = finite(ch?.to)
    if (from == null || to == null) continue
    details.push(`${labeled(`A chord joins ${at(from)} and ${at(to)}`, str(ch.label))}.`)
  }
  const arc = p.arc as Params | undefined
  if (arc && typeof arc === 'object') {
    const from = finite(arc.from)
    const to = finite(arc.to)
    if (from != null && to != null) {
      details.push(
        `${labeled(`The arc from ${at(from)} counterclockwise to ${at(to)} is highlighted`, str(arc.label))}.`,
      )
    }
  }
  if (typeof p.tangentAt === 'number') {
    details.push(`A line tangent to the circle is drawn at ${at(p.tangentAt)}.`)
  }
  if (p.withSquare === 'circumscribed' || p.withSquare === 'inscribed') {
    const base =
      p.withSquare === 'circumscribed'
        ? 'A square is drawn around the circle, each side touching it'
        : 'A square is drawn inside the circle, each corner on it'
    details.push(
      `${str(p.squareLabel) ? `${base}; a side is labeled ${spokenLabel(str(p.squareLabel)!)}` : base}.`,
    )
  }
  return { name: str(p.label) ?? 'Circle', details }
}

/** Shared by solids whose renderers fall back to a letter when a label is absent. */
function dimension(label: unknown, fallback: string): string {
  return spokenLabel(typeof label === 'string' ? label : fallback)
}

function describeTriangle(p: Params): FigureDescription {
  const v = strList(p.vertexLabels)
  const s = strList(p.sideLabels)
  const a = strList(p.angleLabels)
  const details: string[] = []
  const pos = ['bottom-left', 'bottom-right', 'top']
  const vName = (i: number) => (v[i] ? `vertex ${v[i]}` : `the ${pos[i]} vertex`)
  if (v.some(Boolean)) {
    details.push(
      `Vertices: ${list([0, 1, 2].filter((i) => v[i]).map((i) => `${v[i]} at the ${pos[i]}`))}.`,
    )
  }
  const angles = [0, 1, 2]
    .filter((i) => a[i])
    .map((i) => `the angle at ${vName(i)} is labeled ${spokenLabel(a[i]!)}`)
  if (angles.length) details.push(`${capitalize(list(angles))}.`)
  // Side i is opposite vertex i: a = BC, b = CA, c = AB.
  const ends: [number, number][] = [
    [1, 2],
    [2, 0],
    [0, 1],
  ]
  const sides = [0, 1, 2]
    .filter((i) => s[i])
    .map((i) => {
      const [p1, p2] = ends[i]
      const nm =
        v[p1] && v[p2]
          ? `side ${v[p1]}${v[p2]}`
          : i === 2
            ? 'the bottom side'
            : `the side from the ${pos[p1]} vertex to the ${pos[p2]} vertex`
      return `${nm} is labeled ${spokenLabel(s[i]!)}`
    })
  if (sides.length) details.push(`${capitalize(list(sides))}.`)
  return { name: str(p.label) ?? 'Triangle', details }
}

/** Mirrors PolygonShape.tsx's fallback figure (drawn when < 3 usable vertices). */
const DEFAULT_SHAPE = [
  { x: 0, y: 0 },
  { x: 6, y: 0 },
  { x: 5, y: 4 },
  { x: 1, y: 4 },
]

function describeShape(p: Params): FigureDescription {
  const valid = (Array.isArray(p.vertices) ? p.vertices : []).filter(
    (q): q is { x: number; y: number; label?: unknown } =>
      typeof (q as { x?: unknown })?.x === 'number' &&
      typeof (q as { y?: unknown })?.y === 'number',
  )
  // PolygonShape draws a default trapezoid when fewer than 3 usable vertices
  // are given — and still draws the side labels on it — so describe that.
  const verts: { x: number; y: number; label?: unknown }[] =
    valid.length >= 3 ? valid : DEFAULT_SHAPE
  const details: string[] = []
  const n = verts.length
  const labels = strList(p.sideLabels)
  const vl = verts.map((q) => str(q.label))
  const cx = verts.reduce((t, q) => t + q.x, 0) / n
  const cy = verts.reduce((t, q) => t + q.y, 0) / n
  {
    if (vl.some(Boolean))
      details.push(
        `Vertices in order: ${list(vl.map((l, i) => l ?? `unlabeled vertex ${i + 1}`))}.`,
      )
    const edgeName = (i: number) => {
      const a = vl[i]
      const b = vl[(i + 1) % n]
      if (a && b) return `side ${a}${b}`
      // Unlabelled: name the side by where it sits, as a sighted student would.
      const mx = (verts[i].x + verts[(i + 1) % n].x) / 2 - cx
      const my = (verts[i].y + verts[(i + 1) % n].y) / 2 - cy
      const deg = (Math.atan2(my, mx) * 180) / Math.PI
      return `the ${circlePosition(deg).replace(' ', '-')} side`
    }
    // What a sighted student can see: horizontal/vertical sides and parallel pairs.
    const dir = (i: number) => {
      const a = verts[i]
      const b = verts[(i + 1) % n]
      return { dx: b.x - a.x, dy: b.y - a.y }
    }
    const orient: string[] = []
    for (let i = 0; i < n; i++) {
      const { dx, dy } = dir(i)
      if (Math.abs(dy) < EPS) orient.push(`${edgeName(i)} is horizontal`)
      else if (Math.abs(dx) < EPS) orient.push(`${edgeName(i)} is vertical`)
    }
    const parallel: string[] = []
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = dir(i)
        const b = dir(j)
        if (
          Math.abs(a.dx * b.dy - a.dy * b.dx) <
          EPS * Math.max(1, Math.hypot(a.dx, a.dy) * Math.hypot(b.dx, b.dy))
        )
          parallel.push(`${edgeName(i)} is parallel to ${edgeName(j)}`)
      }
    }
    if (orient.length || parallel.length)
      details.push(`${capitalize(list([...orient, ...parallel]))}.`)
    const sideItems = labels
      .map((l, i) => (l && i < n ? `${edgeName(i)} is labeled ${spokenLabel(l)}` : null))
      .filter((x): x is string => !!x)
    if (sideItems.length) details.push(`${capitalize(list(sideItems))}.`)
    const right = numList(p.rightAngles).filter((i) => i >= 0 && i < n)
    if (right.length) {
      details.push(
        `Right-angle marks at ${list(right.map((i) => (vl[i] ? `vertex ${vl[i]}` : `the vertex between ${edgeName((i - 1 + n) % n)} and ${edgeName(i)}`)))}.`,
      )
    }
  }
  const base = n >= 3 ? `Polygon with ${n} sides` : 'Polygon'
  return { name: str(p.label) ?? base, details }
}

function describe(type: string, p: Params): FigureDescription {
  const name = (fallback: string) => str(p.label) ?? fallback
  switch (type) {
    case 'function-graph':
      return describeFunctionGraph(p as FunctionGraphParams)

    case 'right-triangle': {
      const details: string[] = []
      if (p.showRightAngle !== false)
        details.push(
          'The right angle is at the bottom-left vertex, between a horizontal leg and a vertical leg.',
        )
      const sides = [
        str(p.baseLabel) && `the horizontal leg is labeled ${spokenLabel(str(p.baseLabel)!)}`,
        str(p.heightLabel) && `the vertical leg is labeled ${spokenLabel(str(p.heightLabel)!)}`,
        str(p.hypLabel) && `the hypotenuse is labeled ${spokenLabel(str(p.hypLabel)!)}`,
      ].filter((x): x is string => !!x)
      if (sides.length) details.push(`${capitalize(list(sides))}.`)
      const angles = [
        str(p.topAngle) &&
          `the angle at the top vertex is labeled ${spokenLabel(str(p.topAngle)!)}`,
        str(p.bottomRightAngle) &&
          `the angle at the bottom-right vertex is labeled ${spokenLabel(str(p.bottomRightAngle)!)}`,
      ].filter((x): x is string => !!x)
      if (angles.length) details.push(`${capitalize(list(angles))}.`)
      return { name: name('Right triangle'), details }
    }

    case 'right-triangle-altitude': {
      const lab = (k: string, d: string) => str(p[k]) ?? d
      const [A, B, C, D] = [
        lab('aLabel', 'A'),
        lab('bLabel', 'B'),
        lab('cLabel', 'C'),
        lab('dLabel', 'D'),
      ]
      const details = [
        `Triangle ${A}${B}${C} has its right angle at ${C}, the top vertex; ${A}${B} is the hypotenuse along the bottom.`,
        `An altitude is drawn from ${C} straight down to point ${D} on ${A}${B}.`,
      ]
      const seg = [
        str(p.adLabel) && `${A}${D} is labeled ${spokenLabel(str(p.adLabel)!)}`,
        str(p.dbLabel) && `${D}${B} is labeled ${spokenLabel(str(p.dbLabel)!)}`,
        str(p.cdLabel) && `${C}${D} is labeled ${spokenLabel(str(p.cdLabel)!)}`,
        str(p.acLabel) && `${A}${C} is labeled ${spokenLabel(str(p.acLabel)!)}`,
        str(p.bcLabel) && `${B}${C} is labeled ${spokenLabel(str(p.bcLabel)!)}`,
      ].filter((x): x is string => !!x)
      if (seg.length) details.push(`${capitalize(list(seg.map((x) => `segment ${x}`)))}.`)
      return { name: name('Right triangle with an altitude to the hypotenuse'), details }
    }

    case 'triangle':
      return describeTriangle(p)

    case 'circle':
      return describeCircle(p)

    case 'unit-circle': {
      const details: string[] = ['A circle of radius 1 is centered at the origin of the xy-plane.']
      const ang = finite(p.angleDeg) ?? 30
      details.push(
        `A radius makes an angle labeled ${spokenNumber(ang)}° with the positive x-axis, measured counterclockwise, ending at a point on the circle${str(p.pointLabel) ? ` labeled ${spokenLabel(str(p.pointLabel)!)}` : ''}.`,
      )
      if (p.showSinCos === true)
        details.push(
          'From that point, a vertical segment labeled sin drops to the x-axis, and the horizontal segment along the x-axis is labeled cos.',
        )
      const marks = numList(p.marks)
      if (marks.length)
        details.push(`Angle marks at ${list(marks.map((m) => `${spokenNumber(m)}°`))}.`)
      return { name: name('Unit circle'), details }
    }

    case 'cylinder':
      return {
        name: name('Cylinder'),
        details: [
          `The radius of the circular top is labeled ${dimension(p.radiusLabel, 'r')}, and the height is labeled ${dimension(p.heightLabel, 'h')}.`,
        ],
      }

    case 'cone': {
      const details = [
        `The radius of the circular base is labeled ${dimension(p.radiusLabel, 'r')}, and the height, from the tip straight down to the center of the base, is labeled ${dimension(p.heightLabel, 'h')}.`,
      ]
      if (str(p.slantLabel))
        details.push(`The slant height is labeled ${spokenLabel(str(p.slantLabel)!)}.`)
      return { name: name('Cone'), details }
    }

    case 'sphere':
      return {
        name: name('Sphere'),
        details: [
          p.diameterLabel !== undefined
            ? `A diameter through the center is labeled ${dimension(p.diameterLabel, '')}.`
            : `A radius from the center is labeled ${dimension(p.radiusLabel, 'r')}.`,
        ],
      }

    case 'pyramid':
      return {
        name: name('Square pyramid'),
        details: [
          `A side of the square base is labeled ${dimension(p.baseLabel, 's')}, and the height, from the top straight down to the center of the base, is labeled ${dimension(p.heightLabel, 'h')}.`,
        ],
      }

    case 'rectangular-prism':
      return {
        name: name('Rectangular prism'),
        details: [
          `The edges are labeled: width ${dimension(p.widthLabel, 'w')}, height ${dimension(p.heightLabel, 'h')}, and length (depth) ${dimension(p.lengthLabel, 'ℓ')}.`,
        ],
      }

    case 'polygon': {
      const nRaw = typeof p.sides === 'number' ? Math.round(p.sides) : 6
      const n = Math.max(3, Math.min(12, nRaw))
      const details: string[] = []
      const vl = strList(p.vertexLabels)
      if (vl.some(Boolean))
        details.push(`Vertices in order: ${list(vl.filter((x): x is string => !!x))}.`)
      if (str(p.sideLabel))
        details.push(`The bottom side is labeled ${spokenLabel(str(p.sideLabel)!)}.`)
      if (str(p.angleLabel))
        details.push(`One interior angle is labeled ${spokenLabel(str(p.angleLabel)!)}.`)
      return { name: name(`Regular polygon with ${n} sides`), details }
    }

    case 'shape':
      return describeShape(p)

    case 'parallel-lines': {
      const parallel = p.parallel !== false
      const ll = (p.lineLabels ?? {}) as Params
      const top = str(ll.top)
      const bottom = str(ll.bottom)
      const t = str(p.transversalLabel)
      const details: string[] = []
      details.push(
        parallel
          ? `Two horizontal parallel lines${top ? `, ${top} on top` : ''}${bottom ? ` and ${bottom} below` : ''}, are crossed by a transversal${t ? ` labeled ${t}` : ''} that runs from upper left to lower right.`
          : `A horizontal line${top ? ` labeled ${top}` : ''} is crossed by a second line${t ? ` labeled ${t}` : ''} running from upper left to lower right.`,
      )
      const QUAD: Record<string, string> = {
        TL: 'above the line and left of the transversal',
        TR: 'above the line and right of the transversal',
        BL: 'below the line and left of the transversal',
        BR: 'below the line and right of the transversal',
      }
      const angles = (Array.isArray(p.angles) ? (p.angles as Params[]) : [])
        .map((a) => {
          const lab = a && typeof a === 'object' ? str(a.label) : undefined
          if (!lab) return null
          const where =
            parallel && a.at === 'bottom'
              ? bottom
                ? `line ${bottom}`
                : 'the bottom line'
              : top
                ? `line ${top}`
                : 'the top line'
          const q = QUAD[String(a.pos)] ?? QUAD.TR
          return `At ${where}, the angle ${q} is labeled ${spokenLabel(lab)}.`
        })
        .filter((x): x is string => !!x)
      details.push(...angles)
      return {
        name: name(parallel ? 'Two parallel lines cut by a transversal' : 'Two intersecting lines'),
        details,
      }
    }

    case 'number-line': {
      const min = finite(p.min) ?? -5
      const max = finite(p.max) ?? 5
      const details = [
        `A number line from ${spokenNumber(min)} to ${spokenNumber(max)}, with a tick at every integer.`,
      ]
      const iv = p.interval as Params | undefined
      if (iv && typeof iv === 'object') {
        const from = finite(iv.from)
        const to = finite(iv.to)
        if (from != null && to != null) {
          const dot =
            iv.open === true
              ? 'open circles (endpoints not included)'
              : 'filled circles (endpoints included)'
          details.push(
            `The segment from ${spokenNumber(from)} to ${spokenNumber(to)} is shaded, with ${dot}.`,
          )
        }
      }
      const marks = numList(p.marks)
      if (marks.length)
        details.push(
          `Marked point${marks.length > 1 ? 's' : ''} at ${list(marks.map(spokenNumber))}.`,
        )
      return { name: name('Number line'), details }
    }

    case 'box-plot': {
      const vals = [p.min, p.q1, p.median, p.q3, p.max].map(finite)
      const details: string[] = []
      if (vals.every((x) => x != null)) {
        const [mn, q1, md, q3, mx] = vals as number[]
        details.push(
          `Minimum ${spokenNumber(mn)}, first quartile ${spokenNumber(q1)}, median ${spokenNumber(md)}, third quartile ${spokenNumber(q3)}, maximum ${spokenNumber(mx)}.`,
        )
      }
      return { name: name('Box plot'), details }
    }

    case 'dot-plot': {
      const counts = new Map<number, number>()
      if (Array.isArray(p.data)) {
        for (const d of p.data as Params[]) {
          const v = finite(d?.value)
          if (v != null) counts.set(v, (counts.get(v) ?? 0) + (finite(d.count) ?? 1))
        }
      } else {
        for (const v of numList(p.values)) counts.set(v, (counts.get(v) ?? 0) + 1)
      }
      const entries = [...counts.entries()].sort((a, b) => a[0] - b[0])
      const total = entries.reduce((s, e) => s + e[1], 0)
      const details: string[] = []
      if (entries.length) {
        details.push(`${total} dot${total === 1 ? '' : 's'} in all.`)
        details.push(
          `Dots stacked above each value: ${entries.map(([v, c]) => `${spokenNumber(v)} has ${c} dot${c === 1 ? '' : 's'}`).join('; ')}.`,
        )
      }
      return { name: name('Dot plot'), details }
    }

    case 'sentence-diagram': {
      const segs = (Array.isArray(p.segments) ? (p.segments as Params[]) : [])
        .map((sg) => {
          const t = str(sg?.text)
          if (!t) return null
          const k = str(sg.kind)
          return k ? `“${t}” (${k})` : `“${t}”`
        })
        .filter((x): x is string => !!x)
      const details: string[] = []
      if (str(p.title)) details.push(`${str(p.title)}.`)
      if (segs.length) details.push(`Segments in order: ${segs.join(', ')}.`)
      return { name: name('Sentence diagram'), details }
    }

    default:
      return { name: name(type.replace(/-/g, ' ')), details: [] }
  }
}

// Diagram types whose rendered output is already accessible DOM — return
// null so the dispatcher does not wrap them in role="img" (which would hide
// the table/passage content from screen readers).
const READABLE_DOM = new Set(['comparison-table', 'highlighted-passage', 'decision-tree'])

/**
 * The accessible name and full text equivalent of a diagram, or `null` if the
 * diagram already renders as accessible DOM and should be left unwrapped.
 *
 * An author-supplied `params.label` is used as the NAME — it no longer
 * replaces the details, because a hand-written "Graph of two lines" says no
 * more than the generated name did.
 */
export function diagramDescription(diagram: LessonDiagram): FigureDescription | null {
  const p = (diagram?.params ?? {}) as Params
  if (READABLE_DOM.has(diagram.type)) return null
  return describe(diagram.type, p)
}

/** The description as one string (name, then details) — for text-only consumers and tests. */
export function describeDiagram(diagram: LessonDiagram): string | null {
  const d = diagramDescription(diagram)
  if (!d) return null
  return [`${d.name}.`, ...d.details].join(' ')
}
